/**
 * <CancelPaymentMethod> — la carte qui sera débitée, ou son absence (organism).
 *
 * Deux états, un seul bloc : le moyen de paiement par défaut avec sa coche, ou l'alerte qui dit
 * qu'il n'y en a pas et les deux gestes qui la lèvent — mettre à jour sa carte, puis recharger.
 * Sans moyen de paiement, l'étape ne peut pas aboutir : c'est la seule information utile à ce
 * moment-là.
 *
 * Les effets sont injectés (`onUpdateCard`, `onReload`) : le portail Stripe s'ouvre côté ports,
 * jamais ici. Un rappel absent retire son geste.
 *
 * Composition de `<Card>` + `<IconBubble>` + `<AlertCard>` + `<Button>` / `<TextButton>`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { type UICancelPaymentMethod } from "../../../ports/primary/ui/types"
import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Icon } from "../../atoms/icons/Icon"
import { IconBubble } from "../../atoms/icons/IconBubble"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { Card } from "../../molecules/Card"

const MISSING_TITLE = "Aucun moyen de paiement enregistré"
const MISSING_HELP = "Mettez à jour votre carte pour pouvoir régler les frais et finaliser la résiliation."
const UPDATE_LABEL = "Mettre à jour ma carte"
const RELOAD_LABEL = "J'ai mis à jour, recharger"

export type CancelPaymentMethodProps = {
    /** `null` → l'alerte et ses deux gestes. */
    paymentMethod: UICancelPaymentMethod | null
    /** Ouvre le portail de moyens de paiement. Absent → le bouton disparaît. */
    onUpdateCard?: () => void
    /** Relit le moyen de paiement par défaut. Absent → le lien disparaît. */
    onReload?: () => void
    /** La relecture est en cours. */
    reloading?: boolean
    testID?: string
}

export function CancelPaymentMethod(props: CancelPaymentMethodProps) {
    const { paymentMethod, onUpdateCard, onReload, reloading = false, testID } = props

    if (paymentMethod == null) {
        return (
            <Stack gap="3" testID={testID}>
                <AlertCard severity="alert" message={MISSING_TITLE} />
                <Text size="text-sm" color="text-muted">
                    {MISSING_HELP}
                </Text>
                {onUpdateCard != null && (
                    <Button label={UPDATE_LABEL} iconName="credit-card" fullWidth onPress={onUpdateCard} />
                )}
                {onReload != null && (
                    <TextButton label={RELOAD_LABEL} size="sm" fullWidth loading={reloading} onPress={onReload} />
                )}
            </Stack>
        )
    }

    return (
        <Card testID={testID} background="primary-soft" p="3">
            <Row gap="3" align="center">
                <IconBubble iconName="credit-card" color="surface" iconColor="primary" />
                <Stack gap="0" style={styles.body}>
                    <Text size="text-md" weight="bold">
                        {paymentMethod.label}
                    </Text>
                    <Text size="text-sm" color="text-muted">
                        {paymentMethod.detail}
                    </Text>
                </Stack>
                <Icon name="check-circle" size={20} color="success" />
            </Row>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    body: { flexShrink: 1, flexGrow: 1 },
}))
